/**
 * Loyalty Rewards — Wellbi-style claim form
 * Fields always shown: name, order ID, order date, marketplace, review screenshot
 * Backend verifies by platform:
 *   shopify  → orderId + name + review
 *   amazon   → orderId + orderDate + review
 *   flipkart → orderId + orderDate + review
 */
(function () {
  'use strict';

  var REASON_MESSAGES = {
    invalid_input: 'Please fill in all required details.',
    review_required: 'Please upload a screenshot of your Atovio product review.',
    review_invalid_type: 'Please upload a JPG, PNG, WEBP, or HEIC image under 5MB.',
    review_rejected:
      'We could not verify an Atovio product review from that screenshot. Upload a clearer review screenshot.',
    not_found: 'We could not find a matching past order. Double-check your details.',
    confirm_mismatch: 'Those details did not match this order. Try again.',
    ambiguous: 'This order ID matches more than one customer. Select yours below.',
    already_claimed: 'This order has already been used to claim a discount.',
    order_locked: 'Too many incorrect attempts for this order. Please try again later.',
    rate_limited: 'Too many attempts. Please wait a minute and try again.',
    invalid_token: 'Security check failed. Please refresh and try again.',
    system_disabled: 'Claims are temporarily unavailable. Please try again later.',
    system_error: 'Something went wrong creating your discount. Please try again.'
  };

  function qs(root, sel) {
    return root.querySelector(sel);
  }

  function qsa(root, sel) {
    return Array.prototype.slice.call(root.querySelectorAll(sel));
  }

  function escapeHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function formatExpiry(iso) {
    if (!iso) return '';
    try {
      return new Date(iso).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (e) {
      return String(iso);
    }
  }

  function setBusy(btn, busy, idleLabel) {
    if (!btn) return;
    btn.disabled = !!busy;
    btn.setAttribute('aria-busy', busy ? 'true' : 'false');
    btn.textContent = busy ? 'Verifying…' : idleLabel;
  }

  function showResult(root, type, html) {
    var box = qs(root, '[data-loyalty-result]');
    if (!box) return;
    box.hidden = false;
    box.className = 'loyalty-result loyalty-result--' + type;
    box.innerHTML = html;
    box.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }

  function hideResult(root) {
    var box = qs(root, '[data-loyalty-result]');
    if (!box) return;
    box.hidden = true;
    box.innerHTML = '';
  }

  function setFormVisible(root, visible) {
    var form = qs(root, '[data-loyalty-form]');
    var claimBody = qs(root, '[data-loyalty-claim-body]');
    if (form) form.hidden = !visible;
    if (claimBody) claimBody.hidden = !visible;
  }

  function successHtml(data) {
    var d = data.discount || {};
    var code = d.code || data.discountCode || '';
    var pct = d.percentage != null ? d.percentage : data.percentage;
    var expires = formatExpiry(d.expiresAt || data.expiresAt);

    return (
      '<div class="loyalty-success">' +
      '<p class="loyalty-success__title" style="margin-top: 0; margin-bottom: 8px;">Congratulations! You’ve unlocked ' +
      (pct != null ? pct + '% OFF' : 'your reward') +
      '</p>' +
      '<p class="loyalty-success__meta" style="margin-bottom: 8px;">Your exclusive reward is ready!</p>' +
      '<p class="loyalty-success__meta" style="margin-bottom: 8px;">' +
      '<strong>Copy your code and apply it at checkout</strong> to enjoy ' +
      (pct != null ? pct + '% OFF' : 'your discount') +
      ' on your purchase of <strong>atovio Nino or atovio X.</strong>' +
      '</p>' +
      '<div class="loyalty-success__code-wrap" data-loyalty-discount-widget style="margin-bottom: 20px;">' +
      '<code class="loyalty-success__code" data-loyalty-code>' +
      escapeHtml(code) +
      '</code>' +
      '<button type="button" class="loyalty-success__copy" data-loyalty-copy>Copy</button>' +
      '</div>' +
      (expires ? '<p class="loyalty-success__meta" style="margin-bottom: 8px;"><strong>Valid until:</strong> ' + escapeHtml(expires) + '</p>' : '') +
      '<p class="loyalty-success__meta" style="margin-bottom: 8px;"><strong>One-time use only.</strong></p>' +
      '<p class="loyalty-success__hint">Don’t miss out on your exclusive reward!</p>' +
      '</div>'
    );
  }

  function ambiguousHtml(data) {
    var candidates = (data && data.candidates) || [];
    var list = candidates
      .map(function (c, i) {
        return (
          '<label class="loyalty-choice">' +
          '<input type="radio" name="orderRecordId" value="' +
          escapeHtml(c.id) +
          '"' +
          (i === 0 ? ' checked' : '') +
          '>' +
          '<span class="loyalty-choice__body">' +
          '<span class="loyalty-choice__label">' +
          escapeHtml(c.label || 'Customer') +
          '</span>' +
          '<span class="loyalty-choice__meta">' +
          escapeHtml((c.platform || '') + (c.externalOrderId ? ' · ' + c.externalOrderId : '')) +
          '</span>' +
          '</span>' +
          '</label>'
        );
      })
      .join('');

    return (
      '<div class="loyalty-ambiguous" data-loyalty-ambiguous>' +
      '<p class="loyalty-error__title">Multiple matches found</p>' +
      '<p class="loyalty-error__text">Select your details to continue.</p>' +
      '<div class="loyalty-choices" role="radiogroup">' +
      list +
      '</div>' +
      '<button type="button" class="loyalty-submit loyalty-submit--secondary" data-loyalty-confirm>Continue with selected</button>' +
      '</div>'
    );
  }

  function errorMessage(data, status) {
    if (data && data.message) return data.message;
    if (data && data.reason && REASON_MESSAGES[data.reason]) return REASON_MESSAGES[data.reason];
    if (status === 0 || status >= 500) {
      return 'Could not reach the verification server. Check the Verify API URL / tunnel.';
    }
    if (status === 404) {
      return 'Verification endpoint not found. Update Verify API URL in the section settings.';
    }
    return 'Verification failed. Please try again.';
  }

  async function submitVerifyFormData(url, formData) {
    var res = await fetch(url, {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body: formData
    });
    var data = {};
    try {
      data = await res.json();
    } catch (e) {
      data = {};
    }
    return { ok: res.ok && data.verified === true, status: res.status, data: data };
  }

  function isImageFile(file) {
    if (!file) return false;
    if (file.type && file.type.indexOf('image/') === 0) return true;
    var name = String(file.name || '').toLowerCase();
    return /\.(jpe?g|png|webp|heic|heif)$/.test(name);
  }

  function initUploadUI(root, form) {
    var fileInput = qs(form, '[name="reviewScreenshot"]');
    var drop = qs(form, '[data-loyalty-upload]');
    var labelEl = qs(form, '[data-loyalty-upload-label]');
    var metaEl = qs(form, '[data-loyalty-upload-meta]');
    if (!fileInput || !drop) return;

    function refreshUpload() {
      var file = fileInput.files && fileInput.files[0];
      drop.classList.toggle('is-filled', !!file);
      if (labelEl) {
        labelEl.textContent = file ? file.name : 'Tap to upload screenshot';
      }
      if (metaEl) {
        metaEl.textContent = file
          ? Math.max(1, Math.round(file.size / 1024)) + ' KB'
          : 'JPG or PNG · up to 5MB';
      }
    }

    drop.addEventListener('click', function (e) {
      if (e.target === fileInput) return;
      fileInput.click();
    });
    fileInput.addEventListener('change', refreshUpload);
    refreshUpload();
  }

  function initRoot(root) {
    if (root.getAttribute('data-loyalty-ready') === 'true') return;
    root.setAttribute('data-loyalty-ready', 'true');

    var verifyUrl = root.getAttribute('data-verify-url') || '/apps/verify';
    var shop = root.getAttribute('data-shop') || '';
    var form = qs(root, '[data-loyalty-form]');
    var pending = {
      name: '',
      orderId: '',
      orderDate: '',
      platform: '',
      file: null
    };

    if (form) initUploadUI(root, form);

    root.addEventListener('click', function (e) {
      var market = e.target.closest('[data-loyalty-market]');
      if (!market || !form) return;
      var platform = market.getAttribute('data-loyalty-market');
      var select = qs(form, '[name="platform"]');
      if (platform && select) select.value = platform;
    });

    async function runVerify(formData, btn, idle) {
      setBusy(btn, true, idle);
      try {
        var result = await submitVerifyFormData(verifyUrl, formData);
        if (result.ok) {
          setFormVisible(root, false);
          showResult(root, 'success', successHtml(result.data));
          pending = { name: '', orderId: '', orderDate: '', platform: '', file: null };
        } else if (result.data && result.data.reason === 'ambiguous') {
          pending.name = formData.get('name') || pending.name;
          pending.orderId = formData.get('orderId') || pending.orderId;
          pending.orderDate = formData.get('orderDate') || pending.orderDate;
          pending.platform = formData.get('platform') || pending.platform;
          pending.file = formData.get('reviewScreenshot') || pending.file;
          showResult(root, 'info', ambiguousHtml(result.data));
        } else {
          setFormVisible(root, true);
          showResult(
            root,
            'error',
            '<p class="loyalty-error__title">Could not verify</p><p class="loyalty-error__text">' +
              escapeHtml(errorMessage(result.data, result.status)) +
              '</p>'
          );
        }
      } catch (err) {
        setFormVisible(root, true);
        showResult(
          root,
          'error',
          '<p class="loyalty-error__title">Network error</p><p class="loyalty-error__text">Please check your connection and try again.</p>'
        );
      } finally {
        setBusy(btn, false, idle);
      }
    }

    function buildBaseFormData() {
      var fd = new FormData();
      var nameInput = qs(form, '[name="name"]');
      var orderInput = qs(form, '[name="orderId"]');
      var dateInput = qs(form, '[name="orderDate"]');
      var platformInput = qs(form, '[name="platform"]');
      var fileInput = qs(form, '[name="reviewScreenshot"]');

      var name = nameInput ? String(nameInput.value || '').trim() : pending.name;
      var orderId = orderInput ? String(orderInput.value || '').trim() : pending.orderId;
      var orderDate = dateInput ? String(dateInput.value || '').trim() : pending.orderDate;
      var platform = platformInput
        ? String(platformInput.value || '').trim().toLowerCase()
        : pending.platform;
      var file =
        fileInput && fileInput.files && fileInput.files[0] ? fileInput.files[0] : pending.file;

      if (name) fd.append('name', name);
      if (orderId) fd.append('orderId', orderId);
      if (orderDate) fd.append('orderDate', orderDate);
      if (platform) fd.append('platform', platform);
      if (shop) fd.append('shop', shop);
      if (file) fd.append('reviewScreenshot', file, file.name || 'review.jpg');

      return {
        fd: fd,
        name: name,
        orderId: orderId,
        orderDate: orderDate,
        platform: platform,
        file: file
      };
    }

    if (form) {
      form.addEventListener('submit', async function (e) {
        e.preventDefault();
        hideResult(root);
        qsa(root, '[data-hide-on-submit]').forEach(function(el) { el.hidden = true; });
        setFormVisible(root, true);
        var btn = qs(form, '[type="submit"]');
        var idle = btn ? btn.getAttribute('data-idle-label') || 'Claim my reward →' : 'Claim my reward →';
        var built = buildBaseFormData();

        if (!built.name) {
          showResult(
            root,
            'error',
            '<p class="loyalty-error__title">Missing details</p><p class="loyalty-error__text">Enter your name.</p>'
          );
          return;
        }
        if (!built.orderId) {
          showResult(
            root,
            'error',
            '<p class="loyalty-error__title">Missing details</p><p class="loyalty-error__text">Enter your order ID.</p>'
          );
          return;
        }
        if (!built.orderDate) {
          showResult(
            root,
            'error',
            '<p class="loyalty-error__title">Missing details</p><p class="loyalty-error__text">Enter your order date.</p>'
          );
          return;
        }
        if (!built.platform || ['shopify', 'amazon', 'flipkart'].indexOf(built.platform) === -1) {
          showResult(
            root,
            'error',
            '<p class="loyalty-error__title">Missing details</p><p class="loyalty-error__text">Select a marketplace.</p>'
          );
          return;
        }
        if (!built.file || !isImageFile(built.file)) {
          showResult(
            root,
            'error',
            '<p class="loyalty-error__title">Review screenshot required</p><p class="loyalty-error__text">Upload an image of your Atovio product review.</p>'
          );
          return;
        }
        if (built.file.size > 5 * 1024 * 1024) {
          showResult(
            root,
            'error',
            '<p class="loyalty-error__title">File too large</p><p class="loyalty-error__text">Please upload an image under 5MB.</p>'
          );
          return;
        }

        pending = {
          name: built.name,
          orderId: built.orderId,
          orderDate: built.orderDate,
          platform: built.platform,
          file: built.file
        };
        await runVerify(built.fd, btn, idle);
      });
    }

    root.addEventListener('click', function (e) {
      var copyBtn = e.target.closest('[data-loyalty-copy]');
      if (copyBtn) {
        var codeEl = qs(root, '[data-loyalty-code]');
        if (!codeEl) return;
        var text = codeEl.textContent || '';
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(text).then(function () {
            copyBtn.textContent = 'Copied';
            setTimeout(function () {
              copyBtn.textContent = 'Copy';
            }, 1600);
          });
        }
        return;
      }

      var confirmBtn = e.target.closest('[data-loyalty-confirm]');
      if (!confirmBtn) return;

      var built = buildBaseFormData();
      if (!built.name || !built.orderId || !built.platform || !built.file) {
        showResult(
          root,
          'error',
          '<p class="loyalty-error__title">Missing details</p><p class="loyalty-error__text">Fill the form again, then select your match.</p>'
        );
        return;
      }

      var selected = qs(root, 'input[name="orderRecordId"]:checked');
      if (!selected) {
        showResult(
          root,
          'error',
          '<p class="loyalty-error__title">Select a match</p><p class="loyalty-error__text">Choose your details from the list.</p>'
        );
        return;
      }
      built.fd.append('orderRecordId', selected.value);

      var mainBtn = qs(form, '[type="submit"]');
      var idle = mainBtn
        ? mainBtn.getAttribute('data-idle-label') || 'Claim my reward →'
        : 'Claim my reward →';
      runVerify(built.fd, confirmBtn, idle);
    });
  }

  function boot() {
    qsa(document, '[data-loyalty-rewards]').forEach(initRoot);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', function (event) {
    if (!event || !event.target) return;
    qsa(event.target, '[data-loyalty-rewards]').forEach(initRoot);
  });
})();
